import { useMemo, useState } from "react";
import STBoardComments from "../../../assets/styles/organisms/boardComment.module.scss";
import Comment from "./Comment";
import {
  useGetAllRepliesCommentByParentId,
  useConvertCommentTree,
} from "./hooks";
import moment from "moment";
import { useBoard } from "../../../providers/BoardDetailProvider";
import type { Comment as CommentData } from "../../../types/boardDetail";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import { Collapse } from "@material-ui/core";

const DEFAUT_REPLIES_COMMENT_SHOW = 5;

const RepliesComment = (props: any) => {
  const { isLoadingReplies, handleGetAllReplies } =
    useGetAllRepliesCommentByParentId();
  const { repliesComment = [], parentId, totalRepliesComment } = props;
  const repliesCommentLength = repliesComment.length;
  const [showAllRepliesComments, setShowAllRepliesComment] = useState<boolean>(
    repliesCommentLength >= totalRepliesComment
  );
  const onShowAllRepliesComment = async () => {
    if (isLoadingReplies) {
      return;
    }
    if (totalRepliesComment - repliesCommentLength !== 0) {
      await handleGetAllReplies(
        Number(parentId),
        Number(totalRepliesComment + repliesCommentLength)
      );
    }
    setShowAllRepliesComment(true);
  };

  const onCollapseRepliesComment = () => setShowAllRepliesComment(false);

  // 折りたたみ時に常に表示する件数（最新側）。読み込み済みが 5 件を超えていればその分は表示する
  const collapsedCount = useMemo(() => {
    if (totalRepliesComment === repliesCommentLength) {
      return DEFAUT_REPLIES_COMMENT_SHOW;
    }
    return Math.max(repliesCommentLength, DEFAUT_REPLIES_COMMENT_SHOW);
  }, [repliesCommentLength, totalRepliesComment]);

  const countRepliesComment = useMemo(() => {
    if (totalRepliesComment - repliesCommentLength !== 0) {
      return totalRepliesComment - repliesCommentLength;
    }

    return totalRepliesComment - DEFAUT_REPLIES_COMMENT_SHOW;
  }, [repliesCommentLength, totalRepliesComment]);

  // 返信は古いものが上、新しいものが下（時系列）。API・state は新しい順なので、
  // 最新 collapsedCount 件（常時表示）と、それより古い返信（折りたたみ対象）に分けて並べ直す
  const { latestReplies, olderReplies } = useMemo(() => {
    const newestFirst = [...(repliesComment as CommentData[])].sort(
      (a: CommentData, b: CommentData) =>
        moment(b.created_at).valueOf() - moment(a.created_at).valueOf()
    );
    return {
      latestReplies: newestFirst.slice(0, collapsedCount).reverse(),
      olderReplies: newestFirst.slice(collapsedCount).reverse(),
    };
  }, [repliesComment, collapsedCount]);

  return (
    <div className={STBoardComments.newCommentReplies}>
      {!showAllRepliesComments && countRepliesComment > 0 && (
        <div
          className={STBoardComments.showAllRepliesComment}
          onClick={onShowAllRepliesComment}
        >
          <KeyboardArrowDownIcon />
          <span>{`返信をもっと見る(${countRepliesComment})`}</span>
        </div>
      )}
      {showAllRepliesComments &&
        repliesCommentLength > DEFAUT_REPLIES_COMMENT_SHOW && (
          <div
            className={STBoardComments.showAllRepliesComment}
            onClick={onCollapseRepliesComment}
          >
            <KeyboardArrowUpIcon />
            <span>閉じる</span>
          </div>
        )}
      {/* 古い返信は高さアニメーション付きで開閉する（閉じ切ったら DOM から外す） */}
      {olderReplies.length > 0 && (
        <Collapse
          in={showAllRepliesComments}
          timeout="auto"
          appear
          unmountOnExit
        >
          <div className={STBoardComments.newCommentRepliesGroup}>
            {olderReplies.map((e: any) => {
              return (
                <Comment key={`reply_item_${e?.id}`} data={e} isRoot={false} />
              );
            })}
          </div>
        </Collapse>
      )}
      {latestReplies.map((e: any) => {
        return <Comment key={`reply_item_${e?.id}`} data={e} isRoot={false} />;
      })}
    </div>
  );
};

const CommentsList = () => {
  const {
    state: { comments },
  } = useBoard();
  const convertCommentTree = useConvertCommentTree();
  return (
    <div className={STBoardComments.commentWrapper}>
      {Object.entries(convertCommentTree(comments))
        .sort(([_, a], [__, b]) => {
          const commentA = a as any;
          const commentB = b as any;
          const pinnedComparison =
            Number(!!commentB.is_pinned) - Number(commentA?.is_pinned);
          if (pinnedComparison !== 0) return pinnedComparison;
          return (
            moment(commentB?.created_at).valueOf() -
            moment(commentA?.created_at).valueOf()
          );
        })
        .map(([_, element]: [any, any]) => {
          const { children = [], total_reply_comments = 0, id } = element;
          const hasChild = !!total_reply_comments || !!children.length;
          return (
            <div
              key={`parent_${id}`}
              className={STBoardComments.newCommentElementContainer}
            >
              <Comment data={element} isRoot={true} />
              <div
                className={hasChild ? STBoardComments.newCommentReplies : ""}
              >
                {hasChild && (
                  <RepliesComment
                    repliesComment={children}
                    parentId={id}
                    totalRepliesComment={total_reply_comments}
                  />
                )}
              </div>
            </div>
          );
        })}
    </div>
  );
};

export default CommentsList;
